import React from 'react'
import { styled, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import { Text } from '@grupoboticario/flora-react-text'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { CrossCircleIcon, CheckCircleIcon } from '@grupoboticario/flora-react-icons'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const tokenActionableAltContent = '$actionableAltContent'
const tokenActionableAlt = '$actionableAlt'
const tokenActionableContent = '$actionableContent'
const tokenActionableDefault = '$actionableDefault'
const tokenActionableActive = '$actionableActive'
const tokenDisabledBackground = '$disabledBackground'
const tokenDisabledContent = '$disabledContent'
const tokenDisabledAltBackground = '$disabledAltBackground'
const tokenDisabledAltContent = '$disabledAltContent'
const tokenNomInteractivePredominant = '$nomInteractivePredominant'

const TextLabel = styled(Text, {
  position: 'absolute',
  fontSize: '$restricted',
  transform: 'translateX(20px)',
  transition: 'all .2s ease-in-out',
  color: tokenActionableContent,
  variants: {
    checked: {
      true: {
        transform: 'translateX(-20px)',
      },
    },
  },
})

const StyledThumb = styled('div', {
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 16,
  height: 16,
  left: 0,
  borderRadius: '$pill',
  transform: 'translateX(4px)',
  transition: '.2s ease-in-out',
  transitionProperty: 'transform, background-color, color',
  variants: {
    checked: {
      true: {
        transform: 'translateX(28px)',
      },
    },
  },
})

const StyledSwitch = styled(SwitchPrimitive.Root, {
  fontFamily: 'inherit',
  display: 'flex',
  position: 'relative',
  left: 0,
  justifyContent: 'center',
  alignItems: 'center',
  appearance: 'none',
  border: 'none',
  padding: 0,
  width: 48,
  height: 24,
  borderRadius: 40,
  cursor: 'pointer',
  transition: '.2s ease-in-out',
  transitionProperty: 'transform, background-color, color',
  '&:focus': {
    outline: 'none',
    boxShadow: '$outline',
  },
  '&[data-state="checked"]': {
    backgroundColor: tokenActionableDefault,
    [` ${StyledThumb}`]: {
      backgroundColor: tokenActionableContent,
      '& > div': {
        '& > svg > path': {
          fill: tokenActionableDefault,
        },
      },
    },
  },
  '&[data-state="unchecked"]': {
    backgroundColor: tokenActionableActive,
    [` ${StyledThumb}`]: {
      backgroundColor: tokenActionableContent,
      '& > div': {
        '& > svg > path': {
          fill: tokenActionableActive,
        },
      },
    },
  },
  '&[data-disabled]': {
    backgroundColor: tokenDisabledBackground,
    [` ${StyledThumb}`]: {
      backgroundColor: tokenDisabledContent,
      '& > div': {
        '& > svg > path': {
          fill: tokenActionableAlt,
        },
      },
    },
    [` ${TextLabel}`]: {
      color: tokenDisabledContent,
    },
  },
  variants: {
    variant: {
      alt: {
        '&[data-state="checked"]': {
          backgroundColor: tokenActionableAlt,
          [` ${StyledThumb}`]: {
            backgroundColor: tokenActionableAltContent,
            '& > div': {
              '& > svg > path': {
                fill: tokenActionableAlt,
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenActionableAltContent,
          },
        },
        '&[data-state="unchecked"]': {
          backgroundColor: '$actionableAltActive',
          [` ${StyledThumb}`]: {
            backgroundColor: tokenActionableAltContent,
            '& > div': {
              '& > svg > path': {
                fill: tokenActionableAlt,
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenActionableAltContent,
          },
        },
        '&[data-disabled]': {
          backgroundColor: tokenDisabledAltBackground,
          [` ${StyledThumb}`]: {
            backgroundColor: tokenDisabledAltContent,
            '& > div': {
              '& > svg > path': {
                fill: tokenActionableAlt,
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenDisabledAltContent,
          },
        },
      },
    },
    showFeedback: {
      true: {
        '&[data-state="checked"]': {
          backgroundColor: '$success-standard',
          [` ${StyledThumb}`]: {
            backgroundColor: '$nonInteractiveAltPredominant',
            '& > div': {
              '& > svg > path': {
                fill: '$success-standard',
              },
            },
          },
        },
        '&[data-state="unchecked"]': {
          backgroundColor: '$error-standard',
          [` ${StyledThumb}`]: {
            backgroundColor: tokenActionableAlt,
            '& > div': {
              '& > svg > path': {
                fill: '$error-standard',
              },
            },
          },
        },
        '&[data-disabled]': {
          backgroundColor: tokenDisabledBackground,
          [` ${StyledThumb}`]: {
            backgroundColor: tokenDisabledContent,
            '& > div': {
              '& > svg > path': {
                fill: tokenActionableAlt,
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenDisabledContent,
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
  },
  compoundVariants: [
    {
      variant: 'alt',
      showFeedback: true,
      css: {
        '&[data-state="checked"]': {
          backgroundColor: '$success-alt',
          [` ${StyledThumb}`]: {
            backgroundColor: tokenActionableAlt,
            '& > div': {
              '& > svg > path': {
                fill: '$success-alt',
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenNomInteractivePredominant,
          },
        },
        '&[data-state="unchecked"]': {
          backgroundColor: '$error-alt',
          [` ${StyledThumb}`]: {
            backgroundColor: tokenActionableAlt,
            '& > div': {
              '& > svg > path': {
                fill: '$error-alt',
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenNomInteractivePredominant,
          },
        },
        '&[data-disabled]': {
          backgroundColor: tokenDisabledAltBackground,
          [` ${StyledThumb}`]: {
            backgroundColor: tokenDisabledAltContent,
            '& > div': {
              '& > svg > path': {
                fill: tokenActionableAlt,
              },
            },
          },
          [` ${TextLabel}`]: {
            color: tokenDisabledAltContent,
          },
        },
      },
    },
  ],
})

const Thumb = forwardRef<
  'div',
  FloraVariants<typeof StyledThumb> & { 'data-state'?: string; showTexts?: boolean }
>(({ showTexts, ...props }, forwardedRef) => {
  const checked = props['data-state'] === 'checked'

  return (
    <StyledThumb ref={forwardedRef} checked={checked} {...props}>
      {showTexts && <TextLabel checked={checked}>{checked ? 'LIG' : 'DES'}</TextLabel>}
      {checked ? <CheckCircleIcon size={16} /> : <CrossCircleIcon size={16} />}
    </StyledThumb>
  )
})

/**
 * @deprecated Esta tipagem refere-se a um componente que será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraswitch-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */

export type SwitchProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof StyledSwitch> &
  SwitchPrimitive.SwitchProps &
  SwitchPrimitive.SwitchThumbProps & {
    showFeedback?: boolean
    showTexts?: boolean
  }

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floraswitch-guia-de-migra%C3%A7%C3%A3o--page | Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const Switch = forwardRef<'button', SwitchProps>(
  ({ showFeedback, showTexts = true, variant, disabled, ...props }, forwardedRef) => {
    return (
      <StyledSwitch
        ref={forwardedRef}
        {...props}
        variant={variant}
        showFeedback={showFeedback}
        disabled={disabled}
        data-flora="switch"
      >
        <SwitchPrimitive.Thumb asChild>
          <Thumb showTexts={showTexts} />
        </SwitchPrimitive.Thumb>
      </StyledSwitch>
    )
  },
)
